import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Countdown from '../Countdown';
import ShabbatSection from './ShabbatSection';
import GiftSection from './GiftSection';

const GOOGLE_CALENDAR_URL =
  'https://calendar.google.com/calendar/render?action=TEMPLATE&text=Sara+and+Gavriel%27s+Wedding&dates=20260528T183000/20260529T013000&location=Gioia+Mia%2C+Nahshonim%2C+Israele&ctz=Asia/Jerusalem';

const generateICS = () => {
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Wedding App//EN',
    'BEGIN:VEVENT',
    'DTSTART;TZID=Asia/Jerusalem:20260528T183000',
    'DTEND;TZID=Asia/Jerusalem:20260529T013000',
    "SUMMARY:Sara and Gavriel's Wedding",
    'LOCATION:Gioia Mia\\, Nahshonim\\, Israele',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'wedding.ics';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const DetailsSection: React.FC = () => {
  const { t } = useTranslation();
  const [calDropdownOpen, setCalDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const weddingDate = new Date('2026-05-28T18:30:00+03:00');

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCalDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <section id="details" className="w-full flex items-center justify-center py-16">
        <div className="max-w-4xl px-4">
          <div className="text-center space-y-4">
            <Countdown targetDate={weddingDate} />
            <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
              {/* Add to Calendar with dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setCalDropdownOpen(!calDropdownOpen)}
                  className="cal-btn inline-flex items-center justify-center gap-2 border border-[#2d5a2d] px-6 py-3 rounded-lg hover:bg-[#2d5a2d] transition-colors duration-300 text-base font-medium w-64 whitespace-nowrap"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  {t('add_to_calendar')}
                </button>

                {calDropdownOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden">
                    <a
                      href={GOOGLE_CALENDAR_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setCalDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-800 text-sm font-medium"
                    >
                      {/* Google "G" icon */}
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Google
                    </a>
                    <div className="border-t border-gray-200"></div>
                    <button
                      onClick={() => {
                        generateICS();
                        setCalDropdownOpen(false);
                      }}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-800 text-sm font-medium w-full text-left"
                    >
                      {/* Apple icon */}
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      Apple
                    </button>
                  </div>
                )}
              </div>

              {/* Directions */}
              <a
                href="https://maps.app.goo.gl/ZvEBwtcyw8Wm5LZs6?g_st=ic"
                target="_blank"
                rel="noopener noreferrer"
                className="cal-btn inline-flex items-center justify-center gap-2 border border-[#2d5a2d] px-6 py-3 rounded-lg hover:bg-[#2d5a2d] transition-colors duration-300 text-base font-medium w-64 whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {t('directions')}
              </a>
            </div>
          </div>
        </div>
      </section>
      <ShabbatSection />
      <GiftSection />
    </>
  );
};

export default DetailsSection;
